import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { companyService } from '../services/company.service'
import type { CompanyMemberDocument, FilmDocument } from '@/types'

interface NeighborMember {
  slug: string
  name: string
}

interface UseCompanyMemberResult {
  member: (CompanyMemberDocument & { id: string }) | null
  choreographerFilms: Array<FilmDocument & { id: string }>
  castFilms: Array<FilmDocument & { id: string }>
  crewFilms: Array<FilmDocument & { id: string }>
  prev: NeighborMember | null
  next: NeighborMember | null
  loading: boolean
  error: string | null
}

export function useCompanyMember(slug: string | undefined): UseCompanyMemberResult {
  const [member, setMember] = useState<(CompanyMemberDocument & { id: string }) | null>(null)
  const [choreographerFilms, setChoreographerFilms] = useState<Array<FilmDocument & { id: string }>>([])
  const [castFilms, setCastFilms] = useState<Array<FilmDocument & { id: string }>>([])
  const [crewFilms, setCrewFilms] = useState<Array<FilmDocument & { id: string }>>([])
  const [prev, setPrev] = useState<NeighborMember | null>(null)
  const [next, setNext] = useState<NeighborMember | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setMember(null)
    setChoreographerFilms([])
    setCastFilms([])
    setCrewFilms([])
    setPrev(null)
    setNext(null)
    Promise.all([
      companyService.getMemberBySlug(slug),
      companyService.getFilmsByMemberSlug(slug),
      companyService.getPublishedMembers(),
    ])
      .then(([memberData, filmData, allMembers]) => {
        if (cancelled) return
        setMember(memberData)
        setChoreographerFilms(filmData.choreographerFilms)
        setCastFilms(filmData.castFilms)
        setCrewFilms(filmData.crewFilms)
        const idx = allMembers.findIndex((m) => m.slug === slug)
        if (idx >= 0 && allMembers.length > 1) {
          const prevM = allMembers[(idx - 1 + allMembers.length) % allMembers.length]
          const nextM = allMembers[(idx + 1) % allMembers.length]
          setPrev(prevM ? { slug: prevM.slug, name: prevM.name } : null)
          setNext(nextM ? { slug: nextM.slug, name: nextM.name } : null)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(formatUserFacingError(err, 'Failed to load member'))
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  return { member, choreographerFilms, castFilms, crewFilms, prev, next, loading, error }
}
